import { useRef, useState } from "react";
import { cn } from "@/lib/utils";
import { formatHours, formatSeconds } from "@/lib/format";
import { useTimer } from "@/contexts/TimerContext";
import { useCompleteTask, useSelfCompleteTask, type MyTask } from "@/hooks/use-my-tasks";
import { useTaskCommentCounts } from "@/hooks/use-task-comments";
import { useHighlight } from "@/hooks/use-highlight";
import TimeTrackingPopup from "@/components/time-registration/TimeTrackingPopup";
import TaskEntriesRow from "@/components/time-registration/TaskEntriesRow";
import { TaskComments } from "@/components/project/TaskComments";
import { EditableDeadline } from "./EditableDeadline";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { SrIcon } from "@/components/ui/SrIcon";
import { TaskProgressBar } from "@/components/shared/TaskProgressBar";

function TaskStatusAction({ task }: { task: MyTask }) {
  const [showConfirm, setShowConfirm] = useState(false);
  const completeMut = useCompleteTask();
  const selfCompleteMut = useSelfCompleteTask();
  // VIB-956: PL markerer egne oppgaver «Fullført» rett til godkjent (slipper
  // å sende egne oppgaver til seg selv for godkjenning).
  const isPL = !!task.is_project_lead;

  const handleConfirm = () => {
    if (isPL) selfCompleteMut.mutate(task.id);
    else completeMut.mutate(task.id);
    setShowConfirm(false);
  };

  if (task.task_status === "in_progress" || task.task_status === "not_started") {
    const pending = completeMut.isPending || selfCompleteMut.isPending;
    return (
      <>
        <AlertDialog open={showConfirm} onOpenChange={(open) => !open && setShowConfirm(false)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{isPL ? "Marker som fullført?" : "Send til godkjenning?"}</AlertDialogTitle>
              <AlertDialogDescription>
                {isPL ? (
                  <>
                    Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) markeres
                    som fullført og godkjent. Som prosjektleder trenger du ikke separat godkjenning.
                  </>
                ) : (
                  <>
                    Oppgaven <strong>{task.task_description}</strong> ({task.project_name}) sendes
                    til leder for godkjenning. Du har logget{" "}
                    {formatHours(task.logged_hours)} av {formatHours(task.assigned_hours)} tildelt.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Avbryt</AlertDialogCancel>
              <AlertDialogAction onClick={handleConfirm}>Bekreft</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        <button
          onClick={() => setShowConfirm(true)}
          disabled={pending}
          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium border border-foreground text-foreground hover:bg-muted transition-colors"
          title={isPL ? "Fullfør" : "Send til godkjenning"}
        >
          <SrIcon name="check" className="w-3 h-3" /> {isPL ? "Fullfør" : "Send til godkjenning"}
        </button>
      </>
    );
  }

  return null;
}

interface MyTasksRowProps {
  task: MyTask;
  showProject: boolean;
  userId: string | null;
  onNavigate?: (projectId: string) => void;
  /** VIB-1200: viser "Gå til prosjekt"-knappen i handlings-cellen selv når
   * showProject er false (gruppert visning — prosjektnavnet vises allerede
   * i gruppe-headeren, men raden skal likevel ha en direkte snarvei). */
  showGoToProject?: boolean;
}

/**
 * Single task row in the desktop MyTasksList table. Renders timer controls,
 * status actions, comments toggle and inline-expanding entries log.
 */
export function MyTasksRow({ task, showProject, userId, onNavigate, showGoToProject = false }: MyTasksRowProps) {
  const { activeTimer, elapsedSeconds, startTimer, stopTimer } = useTimer();
  const [showTimePopup, setShowTimePopup] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const timeAnchorRef = useRef<HTMLButtonElement>(null);
  const { data: commentCounts } = useTaskCommentCounts([task.id]);
  const commentCount = commentCounts?.get(task.id) ?? 0;
  const highlightRef = useHighlight<HTMLTableRowElement>([task.id, task.budget_line_id]);

  const isTaskActive = activeTimer?.taskId === task.id;
  const isProjectLocked = task.project_status === "completed";

  const handleTimerToggle = async () => {
    if (isProjectLocked) return;
    if (isTaskActive) {
      await stopTimer();
    } else {
      await startTimer({
        taskId: task.id,
        projectId: task.project_id,
        projectName: task.project_name,
        taskDescription: task.task_description,
        phase: task.phase,
        budgetLineId: task.budget_line_id || null,
        assignmentId: task.id,  // VIB-671: task.id is the assignment.id in MyTask
      });
    }
  };

  const timerDisplay = isTaskActive
    ? formatSeconds(elapsedSeconds)
    : formatHours(task.logged_hours);

  const colSpan = (showProject ? 1 : 0) + 4 + (showProject ? 1 : 0);

  return (
    <>
      <tr ref={highlightRef} className="border-b border-border/50 last:border-0 hover:bg-muted/30">
        {showProject && (
          <td
            className={cn(
              "py-2.5 px-4 font-medium text-xs",
              task.logged_hours > 0 && "cursor-pointer hover:underline"
            )}
            onClick={() => task.logged_hours > 0 && setShowEntries(!showEntries)}
          >
            {task.project_name}
            {task.logged_hours > 0 && (
              showEntries
                ? <SrIcon name="chevron-up" className="w-3 h-3 inline ml-1.5 text-muted-foreground" />
                : <SrIcon name="chevron-down" className="w-3 h-3 inline ml-1.5 text-muted-foreground" />
            )}
          </td>
        )}
        <td className="py-2.5 px-4">
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <span className="text-xs text-muted-foreground">{task.phase}</span>
              <span className="text-xs text-muted-foreground mx-1">—</span>
              <span className="text-xs">{task.task_description}</span>
              {task.notes && (
                <p className="text-[11px] text-muted-foreground italic mt-0.5">{task.notes}</p>
              )}
              <div className="mt-1 max-w-[180px]">
                <TaskProgressBar loggedHours={task.logged_hours} assignedHours={task.assigned_hours} />
              </div>
            </div>
            <EditableDeadline task={task} />
          </div>
        </td>
        <td className="py-2.5 px-4 text-right tabular-nums text-sm">
          {formatHours(task.assigned_hours)}
        </td>
        <td className="py-2.5 px-4">
          {/* Fast-bredde, venstrejustert wrapper sentrert i cellen → start-knappen
              havner alltid på samme x uavhengig av tid-tekstens bredde. */}
          <div className="flex justify-center">
            <div className="flex items-center gap-2 w-[112px]">
              <button
                onClick={handleTimerToggle}
                disabled={isProjectLocked}
                aria-label={isTaskActive ? "Pause" : "Start"}
                title={isProjectLocked ? "Prosjektet er lukket — åpne det på nytt for å logge timer" : undefined}
                className={cn(
                  "w-8 h-8 shrink-0 flex items-center justify-center rounded-full border-[1.5px] transition-colors",
                  isTaskActive
                    ? "bg-primary text-primary-foreground border-primary hover:bg-primary/90"
                    : "bg-card text-foreground border-foreground hover:bg-muted",
                  isProjectLocked && "opacity-40 cursor-not-allowed",
                )}
              >
                <SrIcon name={isTaskActive ? "pause" : "play"} className="w-3.5 h-3.5" />
              </button>
              <button
                ref={timeAnchorRef}
                onClick={() => setShowTimePopup(!showTimePopup)}
                className={cn(
                  "font-mono text-sm cursor-pointer hover:underline text-left py-1 px-1 h-8 flex items-center shrink-0 whitespace-nowrap",
                  isTaskActive && "text-primary font-semibold"
                )}
              >
                {timerDisplay}
              </button>
            </div>
            {showTimePopup && (
              <TimeTrackingPopup
                task={task}
                onClose={() => setShowTimePopup(false)}
                userId={userId}
                assignmentId={task.id}
                anchorRef={timeAnchorRef}
              />
            )}
          </div>
        </td>
        <td className="py-2.5 px-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <TaskStatusAction task={task} />
            <button
              onClick={() => setShowComments(!showComments)}
              className={cn(
                "inline-flex items-center gap-1 text-muted-foreground hover:text-primary",
                showComments && "text-primary",
                commentCount > 0 && !showComments && "text-primary",
              )}
              title="Kommentarer"
              aria-expanded={showComments}
            >
              <SrIcon name="message-square" className="w-3.5 h-3.5" />
              {commentCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[16px] h-[16px] px-1 text-[10px] font-bold rounded-full bg-primary text-primary-foreground tabular-nums">
                  {commentCount}
                </span>
              )}
            </button>
            {/* VIB-1200: gruppert visning (showProject=false) rendrer navigasjonen
                her i stedet for egen kolonne, så colSpan/kolonnebredder ikke endres. */}
            {!showProject && showGoToProject && (
              <button
                type="button"
                aria-label="Gå til prosjekt"
                onClick={() => onNavigate?.(task.project_id)}
                className="inline-flex items-center justify-center w-10 h-10 -my-2 text-muted-foreground hover:text-foreground"
              >
                <SrIcon name="external-link" className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        </td>
        {showProject && (
          <td className="py-2.5 px-4">
            <button
              type="button"
              aria-label="Gå til prosjekt"
              onClick={() => onNavigate?.(task.project_id)}
              className="inline-flex items-center justify-center w-10 h-10 text-muted-foreground hover:text-foreground"
            >
              <SrIcon name="external-link" className="w-3.5 h-3.5" />
            </button>
          </td>
        )}
      </tr>
      {showEntries && (
        <TaskEntriesRow
          userId={userId}
          budgetLineId={task.budget_line_id}
          colSpan={colSpan}
        />
      )}
      {showComments && (
        <tr className="bg-muted/30">
          <td colSpan={colSpan} className="px-4 py-3">
            <TaskComments assignmentId={task.id} />
          </td>
        </tr>
      )}
    </>
  );
}

export default MyTasksRow;
